import React, { useState, useCallback, useMemo } from 'react';
import { isEmpty } from 'lodash';
import classNames from 'classnames';
import MessageFeedback from 'modules/chatbot/components/MessageFeedback/MessageFeedback';
import ExpandedSources from './ExpandedSources';

import styles from './collapsibleSources.module.less';

// Type definitions
export interface SourceItem {
  title: string;
  url: string;
  type: 'BENEFIT_GUIDE' | 'EMPLOYER' | 'WEB';
  snippet?: string;
  masterId?: string;
  employerId?: string;
  sourceType?: 'ai_training' | 'benefit_guide' | 'dbg_attached' | 'web_search';
}

// New API source format
export interface ApiSourceItem {
  type: 'web_search' | 'benefit_guide' | 'dbg_attached' | 'ai_training';
  url?: string;
  id?: string;
  relevancy: number;
  // eslint-disable-next-line camelcase
  plan_name?: string;
  // eslint-disable-next-line camelcase
  document_name?: string;
  category?: string;
  masterId?: string;
  employerId?: string;
}

export interface CollapsibleSourcesProps {
  /** Legacy single reference URL - will be deprecated in favor of sources array */
  reference?: string;
  /** Array of source items with title, URL, and optional snippet */
  sources?: SourceItem[] | ApiSourceItem[];
  /** Message ID for feedback tracking */
  messageId?: string;
  /** Whether the message is still being processed */
  isPending?: boolean;
  /** Optional CSS class name for custom styling */
  className?: string;
  /** Callback when sources are expanded/collapsed */
  onToggle?: (isExpanded: boolean) => void;
  /** Callback when a plan is clicked */
  onPlanClick?: (response: any) => void;
  /** Callback when a document download is initiated */
  onDocumentDownload?: (documentId: string, employerId: string) => void;
  /** Employer ID from chat context for document downloads */
  employerId?: string;
}

// Constants
const CHEVRON_ICON = '▼';
const SINGLE_SOURCE_LABEL = 'View Related Document';
const SOURCES_LABEL_PREFIX = 'Related Documents';

// Source type categorization
const SOURCE_TYPE_LABELS = {
  BENEFIT_GUIDE: 'MICROSITES AND ASSOCIATED RESOURCES',
  EMPLOYER: 'KNOWLEDGE BASE (FAQS & TRAINING DOCS)',
  WEB: 'INTERNET SOURCES',
} as const;

type SourceTypeKey = keyof typeof SOURCE_TYPE_LABELS;

// Utility function to map API source to internal format
const mapApiSourceToSourceItem = (
  apiSource: ApiSourceItem
): SourceItem | null => {
  // Handle the case where API returns plan_name but interface expects planName
  const planName = (apiSource as any).plan_name;
  const documentName = (apiSource as any).document_name;

  switch (apiSource.type) {
    case 'web_search':
      return {
        title: apiSource.url ? new URL(apiSource.url).hostname : 'Web Search',
        url: apiSource.url || '',
        type: 'WEB',
        masterId: apiSource.masterId,
        employerId: apiSource.employerId,
        sourceType: apiSource.type,
      };
    case 'benefit_guide':
    case 'dbg_attached':
      return {
        title:
          documentName ||
          planName ||
          (apiSource.type === 'benefit_guide' ? 'Microsite' : 'Plan Document'),
        url: apiSource.url || `#${apiSource.id}`,
        type: 'BENEFIT_GUIDE',
        masterId: apiSource.id,
        employerId: apiSource.employerId,
        sourceType: apiSource.type,
      };
    case 'ai_training':
      return {
        title: documentName || planName || 'Training Document',
        url: apiSource.url || `#${apiSource.id}`,
        type: 'EMPLOYER',
        masterId: apiSource.id,
        employerId: apiSource.employerId,
        sourceType: apiSource.type,
      };
    default:
      return null;
  }
};

// Utility function to normalize sources to internal format
const normalizeSources = (
  sources: (SourceItem | ApiSourceItem)[] | undefined
): SourceItem[] => {
  if (!sources || !Array.isArray(sources)) {
    return [];
  }
  return sources
    .map((source) => {
      // Check if it's already in the internal format
      if (
        'title' in source &&
        'type' in source &&
        typeof source.type === 'string' &&
        ['BENEFIT_GUIDE', 'EMPLOYER', 'WEB'].includes(source.type)
      ) {
        return source as SourceItem;
      }
      // Map from API format
      return mapApiSourceToSourceItem(source as ApiSourceItem);
    })
    .filter((source): source is SourceItem => source !== null);
};

// Utility function to categorize sources by type
const categorizeSources = (
  sources: SourceItem[]
): Map<SourceTypeKey, SourceItem[]> => {
  const categorizedMap = new Map<SourceTypeKey, SourceItem[]>();

  // Initialize empty arrays for each category
  Object.keys(SOURCE_TYPE_LABELS).forEach((type) => {
    categorizedMap.set(type as SourceTypeKey, []);
  });

  // Group sources by type
  sources.forEach((source) => {
    const existingCategory = categorizedMap.get(source.type as SourceTypeKey);
    if (existingCategory) {
      existingCategory.push(source);
    }
  });

  return categorizedMap;
};

const CollapsibleSources: React.FC<CollapsibleSourcesProps> = ({
  reference,
  sources = [],
  messageId,
  isPending = false,
  className,
  onToggle,
  onPlanClick,
  onDocumentDownload,
  employerId,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Normalize sources first
  const normalizedSources = useMemo(() => normalizeSources(sources), [sources]);

  // Memoized calculations
  const totalSourcesCount = useMemo(() => {
    return normalizedSources.length || (reference ? 1 : 0);
  }, [normalizedSources.length, reference]);

  const hasAnySources = useMemo(() => {
    return !isEmpty(reference) || normalizedSources.length > 0;
  }, [reference, normalizedSources.length]);

  const sourcesLabel = useMemo(() => {
    return `${SOURCES_LABEL_PREFIX} (${totalSourcesCount})`;
  }, [totalSourcesCount]);

  // Event handlers
  const handleToggleExpansion = useCallback(() => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    onToggle?.(newExpandedState);
  }, [isExpanded, onToggle]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToggleExpansion();
      }
    },
    [handleToggleExpansion]
  );

  // Early return if no sources available
  if (!hasAnySources) {
    return null;
  }

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.messageFooter}>
        <MessageFeedback messageId={messageId} isPending={isPending} />

        <div className={styles.sourcesToggleSection}>
          <button
            type="button"
            className={styles.sourcesToggleButton}
            onClick={handleToggleExpansion}
            onKeyDown={handleKeyDown}
            aria-expanded={isExpanded}
            aria-controls="sources-content"
            aria-label={`${
              isExpanded ? 'Collapse' : 'Expand'
            } ${sourcesLabel.toLowerCase()}`}
          >
            <span className={styles.sourcesLabel}>{sourcesLabel}</span>
            <span
              className={classNames(
                styles.expandIcon,
                isExpanded && styles.expandIconRotated
              )}
              aria-hidden="true"
            >
              {CHEVRON_ICON}
            </span>
          </button>
        </div>
      </div>

      {isExpanded && (
        <ExpandedSources
          sources={normalizedSources}
          reference={reference}
          singleSourceLabel={SINGLE_SOURCE_LABEL}
          categorizeSources={categorizeSources}
          sourceTypeLabels={SOURCE_TYPE_LABELS}
          onPlanClick={onPlanClick}
          onDocumentDownload={onDocumentDownload}
          employerId={employerId}
        />
      )}
    </div>
  );
};

export default CollapsibleSources;
